import axios from "axios";
import React, { useEffect, useState, useCallback } from "react";
import { baseURL } from "../../routes/Config";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Snackbar,
    TextField,
    Typography,
    Collapse,
    useTheme,
    useMediaQuery
} from "@mui/material";
import {
    Close as CloseIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    CheckCircle as SuccessIcon
} from "@mui/icons-material";

const InterpretasiManual = ({
                                isOpenModalManualDiagnose,
                                handleCloseManualDiagnosaModal,
                                radiographicId,
                                tooth,
                                problematicTeeth,
                                handleOdontogramDiagnose,
                                customOrderUp,
                                customOrderDown
}) => {
    const [data, setData] = useState({
        toothNumber: "",
        manualDiagnosis: "",
    });

    const [error, setError] = useState({
        open: false,
        message: "",
        severity: "error", // error, warning, info, success
        title: ""
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "error"
    });

    const [fieldErrors, setFieldErrors] = useState({
        toothNumber: "",
        manualDiagnosis: ""
    });

    const [isLoading, setIsLoading] = useState(false);
    const token = sessionStorage.getItem("token");
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const clearError = useCallback(() => {
        setError(prev => ({ ...prev, open: false, message: "", title: "" }));
        setFieldErrors({ toothNumber: "", manualDiagnosis: "" });
    }, []);

    const showError = useCallback((message, title = "Error", severity = "error") => {
        setError({
            open: true,
            message,
            title,
            severity
        });
    }, []);

    const showSnackbar = useCallback((message, severity = "error") => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    }, []);

    useEffect(() => {
        if (tooth.toothId) {
            setData({
                toothNumber: tooth.toothId,
                manualDiagnosis: tooth.prediction
            });
        } else {
            setData({
                toothNumber: "",
                manualDiagnosis: "",
            });
        }
        clearError();
    }, [tooth, clearError]);

    const validateField = useCallback((name, value) => {
        let error = "";

        switch (name) {
            case "toothNumber":
                if (!value) {
                    error = "Nomor gigi harus diisi";
                } else {
                    const num = parseInt(value, 10);
                    if (isNaN(num)) {
                        error = "Nomor gigi harus berupa angka";
                    } else if (
                        !customOrderUp.includes(num) &&
                        !customOrderDown.includes(num)
                    ) {
                        error = "Nomor gigi harus antara " +
                            "(gigi atas) 18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28 " +
                            "atau (gigi bawah) 48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38";
                    }
                }
                break;
            case "manualDiagnosis":
                if (!value.trim()) {
                    error = "Diagnosa harus diisi";
                } else if (value.trim().length < 3) {
                    error = "Diagnosa minimal 3 karakter";
                }
                break;
            default:
                break;
        }

        return error;
    }, []);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setData(prev => ({ ...prev, [name]: value }));

        if (fieldErrors[name]) {
            setFieldErrors(prev => ({ ...prev, [name]: "" }));
        }

        const error = validateField(name, value);
        if (error) {
            setFieldErrors(prev => ({ ...prev, [name]: error }));
        }
    }, [fieldErrors, validateField]);

    const validateForm = useCallback(() => {
        const newFieldErrors = {};
        let isValid = true;

        const toothNumberError = validateField("toothNumber", data.toothNumber);
        if (toothNumberError) {
            newFieldErrors.toothNumber = toothNumberError;
            isValid = false;
        }

        const manualDiagnosisError = validateField("manualDiagnosis", data.manualDiagnosis);
        if (manualDiagnosisError) {
            newFieldErrors.manualDiagnosis = manualDiagnosisError;
            isValid = false;
        }

        setFieldErrors(newFieldErrors);
        return isValid;
    }, [data, validateField]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        clearError();

        if (!validateForm()) {
            showError("Mohon perbaiki kesalahan pada form", "Validasi Gagal", "warning");
            return;
        }

        const isToothIdDuplicated = problematicTeeth.find(
            probTeeth => probTeeth.toothId === Number(data.toothNumber)
        );

        if (isToothIdDuplicated) {
            showError(
                "Terdeteksi duplikasi pada nomor gigi! Silahkan hapus gigi yang terduplikasi dahulu.",
                "Duplikasi Data",
                "warning"
            );
            return;
        }

        if (!token) {
            showError("Token tidak ditemukan. Silakan login ulang.", "Autentikasi Gagal");
            return;
        }

        setIsLoading(true);

        try {
            handleOdontogramDiagnose({
                toothId: data.toothNumber,
                prediction: data.manualDiagnosis,
                isManual: true,
                isVerified: true,
                verificator_note: ''
            }, false);

            showSnackbar("Data berhasil disimpan!", "success");

        } catch (err) {
            console.error("Error occurred:", err);

            let errorMessage = "Terjadi kesalahan yang tidak diketahui";
            let errorTitle = "Error";

            if (err.response) {
                const status = err.response.status;
                const responseData = err.response.data;

                switch (status) {
                    case 400:
                        errorTitle = "Bad Request";
                        errorMessage = responseData.message || "Data yang dikirim tidak valid";
                        break;
                    case 401:
                        errorTitle = "Unauthorized";
                        errorMessage = "Sesi Anda telah berakhir. Silakan login ulang.";
                        break;
                    case 403:
                        errorTitle = "Forbidden";
                        errorMessage = "Anda tidak memiliki izin untuk melakukan aksi ini.";
                        break;
                    case 404:
                        errorTitle = "Not Found";
                        errorMessage = "Data tidak ditemukan.";
                        break;
                    case 500:
                        errorTitle = "Server Error";
                        errorMessage = "Terjadi kesalahan pada server. Silakan coba lagi nanti.";
                        break;
                    default:
                        errorTitle = `Error ${status}`;
                        errorMessage = responseData.message || "Gagal mengirim data";
                }

                if (responseData.message && responseData.message.includes('syntax error')) {
                    errorTitle = "Database Error";
                    errorMessage = "Terjadi kesalahan pada database. Silakan hubungi administrator.";
                }

            } else if (err.request) {
                errorTitle = "Network Error";
                errorMessage = "Tidak ada respons dari server. Periksa koneksi internet Anda.";
            } else {
                errorTitle = "Application Error";
                errorMessage = err.message || "Terjadi kesalahan dalam aplikasi";
            }

            showError(errorMessage, errorTitle);
        } finally {
            setIsLoading(false);
            handleCloseManualDiagnosaModal()
        }
    }, [data, problematicTeeth, token, radiographicId, handleOdontogramDiagnose, clearError, showError, showSnackbar, validateForm]);

    const getErrorIcon = (severity) => {
        switch (severity) {
            case "error":
                return <ErrorIcon />;
            case "warning":
                return <WarningIcon />;
            case "info":
                return <InfoIcon />;
            case "success":
                return <SuccessIcon />;
            default:
                return <ErrorIcon />;
        }
    };

    return (
        <Box>
            <Dialog
                open={isOpenModalManualDiagnose}
                onClose={() => {
                    clearError()
                    handleCloseManualDiagnosaModal()
                }}
                fullScreen={fullScreen}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                        Interpretasi Manual
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            clearError()
                            handleCloseManualDiagnosaModal()
                        }}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Collapse in={error.open}>
                        <Alert
                            severity={error.severity}
                            icon={getErrorIcon(error.severity)}
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={clearError}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                            sx={{ mb: 2 }}
                        >
                            {error.title && <AlertTitle>{error.title}</AlertTitle>}
                            {error.message}
                        </Alert>
                    </Collapse>

                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                            <Box sx={{ flex: '0 0 30%' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    No. Gigi
                                </Typography>
                                <TextField
                                    fullWidth
                                    id="toothNumber"
                                    name="toothNumber"
                                    placeholder="No. gigi"
                                    value={data.toothNumber}
                                    onChange={handleChange}
                                    error={!!fieldErrors.toothNumber}
                                    helperText={fieldErrors.toothNumber}
                                    size="small"
                                    type="number"
                                    inputProps={{ min: 1, max: 32 }}
                                    disabled={isLoading}
                                />
                            </Box>

                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    Tulis Diagnosa
                                </Typography>
                                <TextField
                                    fullWidth
                                    id="manualDiagnosis"
                                    name="manualDiagnosis"
                                    placeholder="Tulis nama penyakit"
                                    value={data.manualDiagnosis}
                                    onChange={handleChange}
                                    error={!!fieldErrors.manualDiagnosis}
                                    helperText={fieldErrors.manualDiagnosis}
                                    size="small"
                                    disabled={isLoading}
                                />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => {
                            clearError()
                            handleCloseManualDiagnosaModal()
                        }}
                        variant="outlined"
                        size="small"
                        disabled={isLoading}
                    >
                        Batalkan
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        size="small"
                        disabled={isLoading}
                        sx={{ minWidth: 80 }}
                    >
                        {isLoading ? "Menyimpan..." : "Selesai"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default InterpretasiManual;